import React from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import IssuedAvrDownloadButton from './IssuedAvrDownloadButton';
import { USER_TYPE_ABS_ID } from '../../constants/';
import AvrComparisonFieldValue from './AvrComparisonFieldValue';

const READINESS = gql`
  query AVRReadinessFromReportView($id: ID!) {
    avrFinalisationReadiness(_id: $id) {
      report_number revision publication_mode
      report_created_at report_approved_at
      reporting_start_day reporting_end_day schedule_periods_json
      approved_finding_count open_finding_count closed_finding_count other_finding_count
      finding_refs blockers warnings information can_issue
      post_approval_version_count material_change_count formatting_only_count administrative_only_count
      requires_reapproval
      change_categories { category count }
      changes_since_approval {
        parent_id finding_reference kind approved_recorded_at current_recorded_at
        fields { field_name label category before after }
        presentation_fields { field_name label category before after }
      }
    }
  }
`;

// The UI hides the button for non-ABS users and PVRs. Server-side role,
// duty-holder, installation and SECE checks are always authoritative.
export const canViewAvrReadiness = (user, report) => Boolean(
  user && report && report.vs_rpt_is_periodic === false &&
  (String(user.user_type?._id || '') === USER_TYPE_ABS_ID ||
   String(user.user_type?.user_type_display_name || '').trim() === 'ABS')
);

export const readinessHeadline = result => {
  if ((result.blockers || []).length) return 'Readiness issues found';
  if ((result.warnings || []).length) return 'Checks complete — review the warnings';
  return 'Readiness checks passed';
};
const day = value => /^\d{4}-\d{2}-\d{2}$/.test(value || '')
  ? value.slice(8,10) + '/' + value.slice(5,7) + '/' + value.slice(0,4)
  : 'Not supplied';
const instant = value => {
  const date = new Date(value || '');
  return !Number.isNaN(date.getTime())
    ? date.toLocaleString('en-GB', {timeZone:'UTC', year:'numeric', month:'short', day:'2-digit',
        hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:false}) + ' UTC'
    : 'Not supplied';
};
const periods = value => {
  try { const x = JSON.parse(value || '[]'); return Array.isArray(x) ? x : []; }
  catch (_) { return []; }
};

const ISSUE = gql`
 mutation FinaliseAndIssueAvr($id: ID!) {
   finaliseAndIssueAvr(_id: $id) {
     report_id revision issued_at source_digest pdf_sha256
   }
 }
`;
export const isIssueApprover = role => /^(approver|admin|administrator)$/i
    .test(String(role || '').trim());
export default function AvrReadinessPanel({ reportId, controlled = false,
  issued = false, role = '' }) {
  const [check, {called, loading, error, data}] = useLazyQuery(READINESS, {
    fetchPolicy:'no-cache', errorPolicy:'none'
  });
  const [issue, {loading: issuing, error: issueError, data: issueData}] = useMutation(ISSUE);
  const result = data?.avrFinalisationReadiness;
  const receipt = issueData?.finaliseAndIssueAvr;
  const blockers = result?.blockers || [];
  const warnings = result?.warnings || [];
  const scheduleDates = periods(result?.schedule_periods_json);
  const changes = result?.changes_since_approval || [];
  const categories = result?.change_categories || [];
  const information = (result?.information || []).filter(
    message => !(result?.publication_mode === 'legacy_issued' &&
      message.startsWith('Historical issued AVR'))
  );
  const legacyIssued = result?.publication_mode === 'legacy_issued';

  return (
    <section className="my-5 rounded-lg border border-blue-200 bg-blue-50 p-4"
      aria-label="Report readiness">
      <h2 className="text-xl font-bold text-blue-900">Report readiness</h2>
      <p className="mb-3 mt-1 text-sm text-gray-800">
        Check the approved findings, dates and any issues before finalisation.
        Nothing will be saved or issued.
      </p>
      <button className="button-red" type="button" disabled={!reportId || loading}
        aria-busy={loading} onClick={() => check({ variables:{ id:reportId } })}>
        {loading ? 'Checking Report...' : called ? 'Check Again' : 'Check Report Readiness'}
      </button>
      {controlled && !issued && isIssueApprover(role) && (
        <div className="my-3 rounded border border-blue-200 bg-white p-3">
          <h3 className="font-semibold text-blue-900">Finalise &amp; Issue</h3>
          <p className="my-2 text-sm text-gray-700">
            This permanently freezes the approved revision and publishes its
            verified PDF to the correct client. The issued document cannot be overwritten.
          </p>
          <button type="button" className="button-red"
            disabled={!result?.can_issue || issuing || Boolean(receipt)}
            aria-busy={issuing}
            onClick={() => {
              if (window.confirm('Finalise and ISSUE this approved AVR to the client? ' +
                  'This action is permanent and will be recorded in the audit log.')) {
                issue({ variables:{ id:reportId } });
              }
            }}>
            {issuing ? 'Finalising...' : 'Finalise & Issue'}
          </button>
          {!result?.can_issue && !receipt &&
            <p className="mt-2 text-sm text-gray-700" role="status">
              This action remains locked until the readiness checks pass and
              ABS explicitly enables the issuing service.
            </p>}
          {issueError && <p className="mt-2 text-sm text-red-800" role="alert">
            Issuing did not complete. The report remains unpublished unless
            an issued receipt is confirmed. Please run readiness again.
          </p>}
          {receipt && <div className="mt-3 text-sm" role="status">
            <strong>Report issued successfully.</strong>
            <p>Revision {receipt.revision} — {receipt.issued_at}</p>
            <p className="break-all">PDF SHA-256: {receipt.pdf_sha256}</p>
            <IssuedAvrDownloadButton reportId={reportId}/>
          </div>}
        </div>
      )}
      {error && <p role="alert" className="mt-3 text-sm text-red-800">
        Readiness check unavailable. Check your ABS permissions or connection and try again.
        No report data was changed.
      </p>}
      {!error && result && <div className="mt-4 rounded border border-gray-200 bg-white p-4"
        aria-live="polite">
        <h3 className="font-bold text-blue-900">{readinessHeadline(result)}</h3>
        <p className="mt-1 text-sm">{result.report_number} — Revision {result.revision}</p>
        {legacyIssued &&
          <p className="mt-3 rounded bg-gray-100 p-3 text-sm" role="status">
            Historical issued AVR — diagnostic check only. The existing report and PDF
            will not be changed or re-issued.
          </p>}
        <h4 className="mt-4 font-semibold">Approved finding versions</h4>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4"
          aria-label="Approved finding counts">
          {[
            ['Approved',result.approved_finding_count],['Open',result.open_finding_count],
            ['Closed',result.closed_finding_count],['Other',result.other_finding_count]
          ].map(([name,value]) => <div className="rounded border bg-gray-50 p-3 text-center" key={name}>
            <strong className="block text-2xl text-blue-900">{value}</strong>
            <span className="text-sm">{name}</span>
          </div>)}
        </div>
        <p className="mt-2 text-sm text-gray-600">
          Counted from approved finding history at the report's approval time.
          Pending revisions do not hide an earlier approved version.
        </p>
        <h4 className="mt-4 font-semibold">Date checks</h4>
        <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
          <div><dt className="font-semibold">Reporting start</dt>
            <dd>{day(result.reporting_start_day)}</dd></div>
          <div><dt className="font-semibold">Reporting end</dt>
            <dd>{day(result.reporting_end_day)}</dd></div>
          <div><dt className="font-semibold">Created (UTC)</dt>
            <dd>{instant(result.report_created_at)}</dd></div>
          <div><dt className="font-semibold">Approved (UTC)</dt>
            <dd>{instant(result.report_approved_at)}</dd></div>
        </dl>
        {scheduleDates.length > 0 && <details className="mt-3 text-sm">
          <summary className="cursor-pointer font-semibold text-blue-900">
            View verification schedule dates
          </summary>
          <ul className="mt-2 list-disc pl-6">
            {scheduleDates.map((s,i) => <li key={s.schedule_id || i}>
              Schedule {i+1}: {day(s.start_day)} to {day(s.end_day)}
            </li>)}
          </ul>
        </details>}

        {/* Distinguish a new history version from an actual finding-field change.
            Disclosure is intentionally read-only and preserves the old issue. */}
        {(result.post_approval_version_count || 0) > 0 && (
          <div className="mt-4 rounded border border-gray-200 p-3">
            <h4 className="font-semibold text-blue-900">Changes recorded since approval</h4>
            <p className="mt-1 text-sm">
              {result.post_approval_version_count} newer approved finding histories;
              {' '}{result.material_change_count} with substantive changes,
              {' '}{result.formatting_only_count} with formatting-only changes and
              {' '}{result.administrative_only_count} with history/audit changes only.
            </p>
            {categories.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-2 text-sm" aria-label="Change categories">
                {categories.map(({category,count}) => (
                  <li key={category} className="rounded border border-gray-300 p-2">
                    {category}: <strong>{count}</strong>
                  </li>
                ))}
              </ul>
            )}
            {result.requires_reapproval && !legacyIssued && (
              <p role="alert" className="mt-3 text-sm font-semibold text-red-900">
                Report approval must be renewed after these substantive changes
                before Finalise &amp; Issue can be permitted.
              </p>
            )}
            {result.requires_reapproval && legacyIssued && (
              <p className="mt-3 text-sm text-gray-700">
                These changes occurred after the original approval. The existing
                issue remains unchanged; review changes before preparing a new revision.
              </p>
            )}
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer font-semibold text-blue-900">
                View Changes Since Approval ({changes.length})
              </summary>
              <p className="mt-2 text-gray-700">
                Each finding compares its approved version at report approval
                against its latest approved history. History-only updates are
                shown separately and do not, by themselves, require reapproval.
              </p>
              <div className="mt-3 space-y-3">
                {changes.map(change => (
                  <details key={change.parent_id}
                    className="rounded border border-gray-200 p-3">
                    <summary className="cursor-pointer font-semibold text-blue-900">
                      {change.finding_reference} — {
                        change.kind === 'history_only' ? 'History record only' :
                        change.kind === 'formatting_only' ? 'Formatting only' :
                        change.kind === 'new_approved_finding' ? 'New approved finding' :
                        change.kind === 'previously_approved_finding_missing'
                          ? 'Previously approved finding unavailable' : 'Substantive change'
                      }
                    </summary>
                    <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
                      <div><dt className="font-semibold">Recorded at report approval</dt>
                        <dd>{instant(change.approved_recorded_at)}</dd></div>
                      <div><dt className="font-semibold">Latest approved history</dt>
                        <dd>{instant(change.current_recorded_at)}</dd></div>
                    </dl>
                    {[...(change.fields || []),...(change.presentation_fields || [])].length === 0 ? (
                      <p className="mt-2 text-gray-600">
                        No report-relevant finding fields changed between these versions.
                      </p>
                    ) : (
                      <div className="mt-3 space-y-4">
                        {[...(change.fields || []),...(change.presentation_fields || [])].map(field => (
                          <div key={field.field_name} className="rounded border p-3">
                            <p className="font-semibold">
                              {field.label} <span className="font-normal text-gray-600">
                                ({field.category})
                              </span>
                            </p>
                            {field.category==='Formatting only' && <p className="text-sm text-gray-600">
                              Text and HTML structure are unchanged; TinyMCE presentation attributes differ.
                            </p>}
                            <div className="mt-2 grid gap-3 sm:grid-cols-2">
                              <div>
                                <strong className="block">At approval</strong>
                                <AvrComparisonFieldValue fieldName={field.field_name} value={field.before}/>
                              </div>
                              <div>
                                <strong className="block">Latest approved</strong>
                                <AvrComparisonFieldValue fieldName={field.field_name} value={field.after}/>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </details>
                ))}
              </div>
            </details>
          </div>
        )}
        {information.length > 0 && <div className="mt-4 text-sm text-gray-700"
          aria-label="Additional information">
          <h4 className="font-semibold">Additional information</h4>
          <ul className="mt-2 list-disc pl-5">
            {information.map((message,index) => <li key={index}>{message}</li>)}
          </ul>
        </div>}
        {blockers.length > 0 && <div className="mt-4 border border-red-300 bg-red-50 p-3"
          role="alert">
          <h4 className="font-bold">Issues to resolve ({blockers.length})</h4>
          <ul className="list-disc pl-5 text-sm">
            {blockers.map((s,i) => <li key={i}>{s}</li>)}
          </ul>
        </div>}
        {warnings.length > 0 && <div className="mt-4 border border-amber-300 bg-amber-50 p-3">
          <h4 className="font-bold">Warnings to review ({warnings.length})</h4>
          <ul className="list-disc pl-5 text-sm">
            {warnings.map((s,i) => <li key={i}>{s}</li>)}
          </ul>
        </div>}
        {!blockers.length && !warnings.length && <p className="mt-4 text-sm">
          No issues detected by the current read-only checks.
        </p>}
        {(result.finding_refs || []).length > 0 && <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-semibold text-blue-900">
            Show approved finding references ({result.finding_refs.length})
          </summary>
          <ul className="mt-2 list-disc pl-6">
            {result.finding_refs.map((s,i) => <li key={s+'-'+i}>{s}</li>)}
          </ul>
        </details>}
        <p className="mt-4 border-t pt-3 text-sm text-gray-700">
          This is a read-only check, not permission to issue.
          Finalise &amp; Issue is a separate controlled action.
        </p>
      </div>}
    </section>
  );
}
